import { formatPhone, isPhoneNumber, sanitizeFullName } from '@bybeam/formatting';
import { checkFeature } from '@bybeam/platform-lib/features/check.js';
import { ApplicantTypeRole, FeatureName } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import PartnerContact from '@platform-ui-common/components/ContactInfo/index.js';
import ErrorDisplay from '@platform-ui-common/components/ErrorDisplay/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import GenericAddressField from '@platform-ui-common/components/GenericAddressField/index.js';
import Message from '@platform-ui-common/components/Message/index.js';
import Modal from '@platform-ui-common/components/Modal/index.js';
import TextInput from '@platform-ui-common/components/TextInput/index.js';
import usePrograms from '@platform-ui-common/hooks/contexts/PartnerPrograms/index.js';
import { fieldUpdate } from '@platform-ui-common/hooks/useForm/actions.js';
import {
  emailField,
  fullNameField,
  optionalField,
  phoneField,
} from '@platform-ui-common/hooks/useForm/fields.js';
import useForm from '@platform-ui-common/hooks/useForm/hook.js';
import { ValidationMode } from '@platform-ui-common/hooks/useForm/types.js';
import useSnackbar from '@platform-ui-common/hooks/useSnackbar/index.js';
import { Mask } from '@platform-ui-common/utilities/format.js';
import { auth } from '@platform-ui/auth/hooks/useAuth/firebase.js';
import { checkProgramHasAnyFeature } from '@platform-ui/utilities/checkFeature.js';
import { verifyBeforeUpdateEmail } from 'firebase/auth';
import { useMemo, useState } from 'react';
import useProfile from '../hooks/useProfile.js';
import { addressFormTransform } from '../utils/address.js';

const UpdateUserModal = ({ onClose }: { onClose: () => void }) => {
  const {
    user,
    updateProfile: {
      update,
      results: { loading },
    },
    partner,
  } = useProfile();

  const { programs = [] } = usePrograms();
  const paymentFeatureEnabled = programs.some((program) =>
    checkProgramHasAnyFeature(program, [
      FeatureName.PaymentsClaimFunds,
      FeatureName.PaymentsPartnerIssued,
    ]),
  );
  const restrictedThirdPartyEditEnabled =
    user?.core?.applicantProfile?.applicantType?.role === ApplicantTypeRole.ThirdParty &&
    checkFeature(partner?.features, FeatureName.UserRestrictThirdPartyEdits);

  const [error, setError] = useState('');

  const { buildObject, flatFormInit, validation } = addressFormTransform(
    user?.core?.applicantProfile?.mailingAddress,
  );

  const {
    dispatch,
    formData,
    errors,
    trySubmit,
    isPristine,
    touchedFields,
    counters: { missingRequiredFields },
  } = useForm(
    {
      fullName: user.core.name,
      email: user.core.email,
      phone: user?.core?.phone ? formatPhone(user.core.phone) : '',
      ...flatFormInit,
      taxId: user?.core.taxId ? user.core.taxId : '',
    },
    {
      fullName: fullNameField(),
      email: emailField(),
      phone: phoneField(false),
      ...validation,
      taxId: optionalField(),
    },
    ValidationMode.OnSubmit,
  );

  const { fullName, email, phone, taxId } = formData;

  const { showSnackbar } = useSnackbar();

  const emailTouched = useMemo(() => !!touchedFields.email, [touchedFields.email]);
  const addressTouched = useMemo(
    () => !!Object.keys(touchedFields).find((key) => key.includes('address')),
    [touchedFields],
  );

  const doMutation = async (): Promise<void> => {
    if (!user?.core?.id) return;
    if (isPristine) return onClose();

    if (emailTouched) {
      try {
        await verifyBeforeUpdateEmail(auth.currentUser, email);
        showSnackbar('Please visit your new email address to verify before proceeding.');
        onClose();
      } catch (error) {
        showSnackbar('Failed to send verification email. Please try again.');
      }
    } else {
      update({
        variables: {
          input: {
            name: sanitizeFullName(fullName),
            ...(phone && { phone: formatPhone(phone, 'E164') }),
            ...(taxId && { taxId }),
            ...(addressTouched && { mailingAddress: buildObject(formData) }),
          },
        },
      })
        .then((response) => {
          if (response?.data?.user?.updateProfile?.metadata?.status === 200) {
            showSnackbar('Successfully updated');
            onClose();
          }
        })
        .catch(() => setError('Something went wrong'));
    }
  };

  return (
    <Modal title="Update your information" isOpen onClickClose={onClose} size="M">
      <FormContainer
        onSubmit={() => trySubmit(doMutation)}
        buttons={
          <div className="flex flex-col gap-6 items-center">
            <ErrorDisplay errors={missingRequiredFields} visible={missingRequiredFields > 0} />
            <ErrorDisplay message={error} visible={!!error} />
            <Button type="submit" loading={loading} disabled={loading}>
              Submit
            </Button>
          </div>
        }
      >
        <TextInput
          id="fullName"
          label="Full Name"
          onChange={(value): void => dispatch(fieldUpdate('fullName', value))}
          value={fullName}
          error={errors.fullName as string}
          type="text"
          required
          disabled={restrictedThirdPartyEditEnabled || emailTouched}
        />
        <TextInput
          id="email"
          label="Email"
          onChange={(value): void => dispatch(fieldUpdate('email', value))}
          value={email}
          error={errors.email as string}
          type="email"
          required
        />
        <TextInput
          id="phone"
          label="Phone"
          onChange={(value): void => {
            const formatted = isPhoneNumber(value) ? formatPhone(value) : value;
            dispatch(fieldUpdate('phone', formatted));
          }}
          value={phone}
          error={errors.phone as string}
          type="tel"
          disabled={emailTouched}
        />
        <GenericAddressField
          answers={formData}
          field={{ key: 'address', validation: { required: false } }}
          dispatch={dispatch}
          errors={errors}
          showManualInput={false}
          disabled={emailTouched}
        />
        <TextInput
          id="taxId"
          label="Social Security Number"
          onChange={(value): void => dispatch(fieldUpdate('taxId', value))}
          value={taxId}
          error={errors.taxId as string}
          mask={Mask.SSN}
          disabled={emailTouched}
        />
        {restrictedThirdPartyEditEnabled && (
          <Message variant="info">
            You are currently restricted from editing certain profile information. Please contact
            the program team for assistance at{' '}
            <PartnerContact email={partner?.email} phone={partner?.phone} />.
          </Message>
        )}
        {emailTouched && (
          <Message variant="info">
            If you are changing your email, you will not be able to modify any other profile
            information until you verify the new email address. Please visit your new email and
            follow the link to verify.
          </Message>
        )}
        {paymentFeatureEnabled && addressTouched && !emailTouched && (
          <Message variant="info">
            Changing your address may impact future payments. Please contact your case manager to
            confirm the status of your upcoming payments.
          </Message>
        )}
      </FormContainer>
    </Modal>
  );
};

export { UpdateUserModal };
